import { Context, createContext, useContext, useEffect, useState } from "react";
import { UserInfo } from "../types/user.types";
import { useCookies } from 'react-cookie';
import { clearJoinCheckoutAttempt } from '../utils/join';

export interface UserStateContextType {
    clearUserInfo: () => void;
    getUserInfo: () => UserInfo | null;
    setUserInfo: (userInfo: UserInfo) => void;
    updateUserInfo: (updatedInfo: UserInfo) => void;
    isUserLoaded: boolean;  // State to track if user info is fully loaded
    isLoggedIn: boolean;
    isUserStateLoading: boolean;     // State to track loading status
}

export interface UserStateProviderProps {
    children: JSX.Element | JSX.Element[];
}

const UserStateContext: Context<UserStateContextType> = createContext<UserStateContextType>(null!);

const UserStateProvider = ({ children }: UserStateProviderProps) => {
    const [cookies, setCookie, removeCookie] = useCookies(['user']);
    const [user, setUser] = useState<UserInfo | null>(null);
    const [isUserLoaded, setIsUserLoaded] = useState<boolean>(false);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [isUserStateLoading, setisUserStateLoading] = useState<boolean>(true);

    useEffect(() => {
        const initializeUserState = async () => {
            setisUserStateLoading(true); // Start loading
            const storedUser = cookies.user as Partial<UserInfo> | undefined;
            if (storedUser?.userId && storedUser.token) {
                setUser(storedUser as UserInfo);
                setIsLoggedIn(true);
                setIsUserLoaded(true);
            } else {
                if (storedUser) {
                    removeCookie('user', { path: '/' });
                }
                setUser(null);
                setIsLoggedIn(false);
                setIsUserLoaded(false);
            }
            setisUserStateLoading(false); // End loading
        };

        initializeUserState();
    }, [cookies.user, removeCookie]);

    const clearUserInfo = (): void => {
        clearJoinCheckoutAttempt();
        removeCookie('user', { path: '/' });
        setUser(null);
        setIsUserLoaded(false);  // Reset loading state when user is cleared
        setIsLoggedIn(false);
    };

    const setUserInfo = (user: UserInfo): void => {
        if (!user.userId || !user.token) {
            clearUserInfo();
            return;
        }
        setUser(user);
        setCookie('user', user, { path: '/' });
        setIsUserLoaded(true);
        setIsLoggedIn(true);
    };

    const getUserInfo = (): UserInfo | null => {
        return user;
    };

    const updateUserInfo = (updatedInfo: UserInfo): void => {
        if (!updatedInfo.userId || !updatedInfo.token) {
            clearUserInfo();
            return;
        }
        setUser(updatedInfo);
        setCookie('user', updatedInfo, { path: '/' });
    };

    const ctx = {
        clearUserInfo,
        setUserInfo,
        getUserInfo,
        updateUserInfo,
        isUserLoaded,  // Expose the loading state
        isLoggedIn,
        isUserStateLoading,     // Expose the loading state
    };

    return (
        <UserStateContext.Provider value={ctx}>
            {children}
        </UserStateContext.Provider>
    );
};

export const useUserState = () => {
    return useContext(UserStateContext);
};

export default UserStateProvider;
